"use strict";

import { Application } from "../main/application.js";
import { Screen } from "../systems/core/screen.js";
import { AssetImporter } from "../systems/core/assetImporter.js";
import { UIObjectCreator } from "./controllers/uiObjectCreator.js";
import { UITransformHandler } from "./controllers/uiTransformHandler.js";
import { Vector2 } from "../math/vectors.js";

export class UISprite extends PIXI.Sprite {
    constructor(config) {
        // Create the main Object
        let texture = undefined;

        if (config.textureID !== undefined) {
            if (Array.isArray(config.textureID)) {
                texture = AssetImporter.sheetFind(config.textureID[0], config.textureID[1]);
            } else {
                texture = AssetImporter.resourcesFind(config.textureID).texture;
            }
        } else if (config.texture !== undefined) {
            texture = config.texture;
        } else {
            let tempGraphics = new PIXI.Graphics();
            tempGraphics.beginFill(0xFFFFFF);
            tempGraphics.drawPolygon([
                { x: -0.5, y: -0.5 },
                { x: 0.5, y: -0.5 },
                { x: 0.5, y: 0.5 },
                { x: -0.5, y: 0.5 }
            ]);
            tempGraphics.endFill();
            
            texture = Application.main.renderer.generateTexture(tempGraphics);
            tempGraphics.destroy();
        }

        super(texture);

        this.name = config.name;
        
        // Create Parenting
        UIObjectCreator.createParenting(this, config);

        // Set Original Size/AspectRatio
        this._originalSize = { width: this.width, height: this.height };
        this._originalAspectRatio = this._originalSize.width / this._originalSize.height;

        // Create Rect
        UIObjectCreator.createRect(this, config);

        UIObjectCreator.setAspectRatioBehaviour(this, config);

        // Set Rotation Angle
        UIObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        UIObjectCreator.setVisibility(this, config);
        // Set Renderability
        UIObjectCreator.setRenderability(this, config);
        // Set Interactibility
        UIObjectCreator.setInteractibility(this, config);

        UIObjectCreator.setResolutionListeners(this, false, config);
        UIObjectCreator.setOrientationListeners(this, false, config);

        UIObjectCreator.addGenericTransformations(this, config);

        config.onInitialize?.bind(this)(config);

        this.updateNow();
    }

    get widthUI() {
        return this.parentContainer.transformFromUI(this.width);
    }
    get heightUI() {
        return this.parentContainer.transformFromUI(this.height);
    }

    get lossyScaleX() {
        return this.rect.scale.x * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.rect.scale.y * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get preferredBoundX() {
        return this.width * this.lossyScaleX;
    }
    get preferredBoundY() {
        return this.height * this.lossyScaleY;
    }
    get preferredBounds() {
        return { width: this.preferredBoundX, height: this.preferredBoundY };
    }

    dispose() {
        this.renderable = false;
        this.parentContainer?.removeChild(this);
        this.destroy();
    }

    // Set Parent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.updateNow();
    }

    setRect(newRect) {
        UITransformHandler.setRect(this, newRect);
        this.updateNow();
    }

    getPosition() {
        return { x: this.x, y: this.y };
    }
    setPosition(x,y) {
        this.x = x.x ?? x;
        this.y = x.y ?? y;
    }
    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return this.parentContainer.toGlobal(this.getPosition());
    }
    getWorldPosition(invertY) {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        if (invertY) {
            position.y = -position.y;
        }
        return Vector2.divide(position, Screen.main.stageScale);
    }

    transformToLocal(position) {
        return this.toLocal(position);
    }

    _refreshTransform() {
        if (this.parentContainer === undefined) return;

        this.scale.set(1, 1);
        
        UITransformHandler.updateGraphicalTransform(this, {...this.parentContainer.preferredBounds});

        switch(this.preserveAspectRatio.mode) {
            case "widthControlsHeight":
                UITransformHandler.ARWidthControlsHeight(this);
                break;
            case "heightControlsWidth":
                UITransformHandler.ARHeightControlsWidth(this);
                break;
            case "fitInParent":
                UITransformHandler.ARFitInParent(this);
                break;
            case "envelopParent":
                UITransformHandler.AREnvelopParent(this);
                break;
        }

        this.calculatedRect.size.width = this.transformFromUI(this.width)
        this.calculatedRect.size.height = this.transformFromUI(this.height);

        this.width *= this.rect.scale.x;
        this.height *= this.rect.scale.y;
    }

    onResolutionChanged() {
        this._onResolutionWillChangeCallback?.(this);

        this._refreshTransform();
        
        this._onResolutionChangedCallback?.(this);
    }

    onOrientationChanged() {
        this._onOrientationChangedCallback?.(this);
    }

    updateNow() {
        this.onResolutionChanged();
    }

    setTextureID(newTextureID) {
        if (this.texture !== undefined && this.texture.textureCacheIds[0] == newTextureID) return;
        let previousScale = { x: this.scale.x, y: this.scale.y };
        if (Array.isArray(newTextureID)) {
            this.texture = AssetImporter.sheetFind(newTextureID[0], newTextureID[1]);
        } else {
            this.texture = AssetImporter.resourcesFind(newTextureID).texture;
        }
        this._onTextureChanged(previousScale);
    }
    setTexture(newTexture) {
        if (newTexture.texture !== undefined) {
            newTexture = newTexture.texture;
        }
        if (this.texture == newTexture) return;
        let previousScale = { x: this.scale.x, y: this.scale.y };
        this.texture = newTexture;
        this._onTextureChanged(previousScale);
    }
    _onTextureChanged(previousScale) {
        this.scale.set(1,1);
        this._originalSize.width = this.width;
        this._originalSize.height = this.height;
        this._originalAspectRatio = this._originalSize.width / this._originalSize.height;
        this.scale.set(previousScale.x, previousScale.y);
        this._sizeRatio = this.width / this.height;
        this.updateNow();
    }
}